import { Button, IconHelpSupport } from "@studyplus/boron-ui";
import { Field, Form, FormikBag, FormikProps, withFormik } from "formik";
import * as React from "react";
import { AiAssistantTemplateInterface } from "../../../features/SectionsSettingsAiAssistantTemplatesPage/useFetchAiAssistantTemplates";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import ErrorText from "../../atoms/ErrorText/index";
import Input from "../../atoms/Input/index";
import Textarea from "../../atoms/Textarea/index";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../general/Popover/Popover";
import { type OptionType, SelectWrapper } from "../../general/SelectWrapper";
import styles from "./styles.scss";
import validationSchema, {
  type AiAssistantTemplateFormValues,
  DEFAULT_PERIOD_OPTIONS,
  type DefaultPeriod,
} from "./validationSchema";

export enum AiAssistantTemplateFormType {
  Edit,
  New,
}

interface ComponentProps {
  type: AiAssistantTemplateFormType;
  aiAssistantTemplate: AiAssistantTemplateInterface | null;
  submitting: boolean;
  loading: boolean;
  apiErrors: ApiErrorInterface[];
  onSubmit: (values: AiAssistantTemplateFormValues) => void;
  onDelete?: () => void;
}

type Props = ComponentProps & FormikProps<AiAssistantTemplateFormValues>;

const selectOptions: OptionType[] = DEFAULT_PERIOD_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}));

const findSelectOption = (value: DefaultPeriod): OptionType | undefined =>
  selectOptions.find((option) => option.value === value);

const AiAssistantTemplateForm: React.FC<Props> = (props) => {
  const handleDeleteBtn = () => {
    if (
      props.onDelete &&
      props.aiAssistantTemplate &&
      window.confirm("このテンプレートを削除してよろしいですか")
    ) {
      props.onDelete();
    }
  };

  const shouldRenderError = (name: string) => {
    const errors = (props.errors as Record<string, string>)[name];
    const touched = (props.touched as Record<string, boolean>)[name];
    return errors && touched;
  };

  const handlePeriodChange = (option: OptionType | null) => {
    if (option) {
      props.setFieldValue("default_period", option.value);
    }
  };

  return (
    <Form className={styles.root}>
      <div className="mb-6">
        <div className={styles.label}>
          <label htmlFor="ai_assistant_template__name">テンプレート名</label>
        </div>
        <Field
          id="ai_assistant_template__name"
          name="name"
          placeholder="テンプレート名を入力"
          component={Input}
          hasError={shouldRenderError("name")}
        />
        {shouldRenderError("name") && (
          <ErrorText className="mt-1">
            {(props.errors as Record<string, string>).name}
          </ErrorText>
        )}
      </div>
      <div className={styles.infoBox}>
        <p className="text-lg font-bold text-black mb-2">
          AIアシスタントテンプレートとは？
        </p>
        <p className="text-lg leading-[1.4] text-black">
          AIアシスタントがカルテを生成する際に参考にする情報です。塾の指導方針や面談の目的など用途に合わせてカスタマイズでき、その内容を加味してカルテの下書きが作成されます。
          <br />
          AIへの指示は自然な日本語で記述してください。「〜してください」「〜を重視する」などの表現が有効です。具体的なトーンや禁止事項も記載できます。
        </p>
      </div>
      <div className="mb-6">
        <div className={styles.label}>
          <label htmlFor="ai_assistant_template__template">
            面談の背景・方針
          </label>
        </div>
        <Field
          id="ai_assistant_template__template"
          name="template"
          component={Textarea}
          placeholder={`面談の背景・方針を入力\n例：「生徒の成長や努力を前向きな言葉で伝え、自己効力感を高めた指導を重視しています」\n「短期・高頻度での伴走面談」「受験直前集中面談」「自発性の引き出し面談」など`}
          rows="6"
          hasError={shouldRenderError("template")}
        />
        {shouldRenderError("template") && (
          <ErrorText className="mt-1">
            {(props.errors as Record<string, string>).template}
          </ErrorText>
        )}
      </div>
      {props.apiErrors.length > 0 && (
        <div className="mb-6">
          {props.apiErrors.map((err: ApiErrorInterface, idx: number) => (
            <p
              key={`ai_assistant_template-error-${idx}`}
              className="text-red-500 mt-1"
            >
              {err.message}
            </p>
          ))}
        </div>
      )}
      <div className={styles.footer}>
        <div>
          {props.type === AiAssistantTemplateFormType.Edit &&
            props.aiAssistantTemplate && (
              <Button
                disabled={props.submitting}
                type="button"
                className={styles.footerDeleteButton}
                onClick={handleDeleteBtn}
              >
                削除
              </Button>
            )}
        </div>
        <div className={styles.footerRight}>
          <div className={styles.periodWrapper}>
            <div className="flex items-center gap-1 mb-1">
              <span className={styles.periodLabel}>参照期間設定</span>
              <PopoverProvider>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center bg-transparent border-none p-0 cursor-pointer"
                      aria-label="参照期間設定のヘルプ"
                    >
                      <IconHelpSupport className="h-[1.4rem] w-[1.4rem] text-gray-800" />
                    </button>
                  </PopoverTrigger>
                  <PopoverPortal>
                    <PopoverContent side="top" sideOffset={5}>
                      <p>設定期間内の学習データを</p>
                      <p>分析してカルテを作成します</p>
                    </PopoverContent>
                  </PopoverPortal>
                </Popover>
              </PopoverProvider>
            </div>
            <SelectWrapper
              value={findSelectOption(props.values.default_period)}
              options={selectOptions}
              onChange={handlePeriodChange}
              size="md"
              fontSize="md"
              isSearchable={false}
            />
          </div>
          <Button
            disabled={props.submitting || !props.isValid}
            type="submit"
            className={styles.footerButton}
          >
            {props.type === AiAssistantTemplateFormType.Edit ? "更新" : "登録"}
          </Button>
        </div>
      </div>
    </Form>
  );
};

const mapPropsToValues = (
  props: ComponentProps,
): AiAssistantTemplateFormValues => {
  if (props.aiAssistantTemplate) {
    return {
      name: props.aiAssistantTemplate.name,
      template: props.aiAssistantTemplate.template,
      default_period: props.aiAssistantTemplate.defaultPeriod,
    };
  }
  return { name: "", template: "", default_period: "two_weeks" };
};

const handleSubmit = (
  values: AiAssistantTemplateFormValues,
  formikBag: FormikBag<ComponentProps, AiAssistantTemplateFormValues>,
) => {
  if (formikBag.props.submitting) {
    return;
  }
  formikBag.props.onSubmit(values);
};

export default withFormik({
  mapPropsToValues,
  handleSubmit,
  validationSchema,
})(AiAssistantTemplateForm);
